import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { usePermesso } from '../../../context/PermessiContext';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { apiFetch } from '../../../lib/api';
import { oggiRoma } from '../../../utils/dateRoma';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import {
  ColonninaNome, DettaglioSessione, DipendenteOpzione, ElencoSessioni, MOD_SESSIONI, STATO_SESSIONE,
  SedeOpzione, Sessione, StatoSessione,
  etichettaMese, formatDataOra, messaggioErrore, whInKwh,
} from './tipi';
import { Campo, Kpi } from './componenti';
import { API_DOCS } from './SessioniRicaricaPage.api';

const NOTA_MAX = 500;
const ENERGIA_MAX_WH = 1000000;
const PREZZO_MAX = 9999.99;
const MESI_ELENCO = 24;

interface OpzioneMese { valore: string; anno: number; mese: number }

interface FormConferma {
  dipendenteId: string;
  energiaKwh: string;
  fineAt: string; // valore dell'input datetime-local, vuoto se la sessione ha già una fine
  prezzoKwh: string;
  nota: string;
}

interface ErroreApi extends Error { status?: number }

// Ultimi mesi, dal corrente (ora italiana) a ritroso
function mesiElenco(): OpzioneMese[] {
  const [anno, mese] = oggiRoma().split('-').map(Number);
  const risultato: OpzioneMese[] = [];
  for (let i = 0; i < MESI_ELENCO; i++) {
    const indice = anno * 12 + (mese - 1) - i;
    const a = Math.floor(indice / 12);
    const m = (indice % 12) + 1;
    risultato.push({ valore: `${a}-${String(m).padStart(2, '0')}`, anno: a, mese: m });
  }
  return risultato;
}

function nomeCompleto(d: { nome: string; cognome: string }): string {
  return `${d.cognome} ${d.nome}`;
}

function formDa(d: DettaglioSessione, nota = ''): FormConferma {
  return {
    dipendenteId: d.dipendente ? String(d.dipendente.id) : '',
    energiaKwh: d.energiaWh !== null ? String(whInKwh(d.energiaWh)) : '',
    fineAt: '',
    prezzoKwh: '',
    nota,
  };
}

async function leggiDettaglio(id: number): Promise<DettaglioSessione> {
  // @api GET /api/ricarica/sessioni/:id -> {success:true,data:{id,transactionId,colonnina,connettore,dipendente,codiceBadge,inizioAt,fineAt,energiaWh,prezzoKwh,importo,stato,motivoAnomalia,tardiva,competenza,meterStartWh,meterStopWh,motivoStop,ultimoEventoAt,tariffaMancante,log:[{createdAt,utenteName,azione,dettaglio}]}}
  const res = await apiFetch<{ success: boolean; data: DettaglioSessione }>(`/api/ricarica/sessioni/${id}`);
  return res.data;
}

// ── Modale di dettaglio e conferma ───────────────────────────────────────────

interface ModaleSessioneProps {
  riga: Sessione;
  dipendenti: DipendenteOpzione[];
  canEdit: boolean;
  onChiudi: () => void;
  onAggiornata: () => void;
}

function ModaleSessione({ riga, dipendenti, canEdit, onChiudi, onAggiornata }: ModaleSessioneProps) {
  const [dettaglio, setDettaglio] = useState<DettaglioSessione | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [form, setForm] = useState<FormConferma | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState('');
  const [errore, setErrore] = useState('');
  const [avviso, setAvviso] = useState('');
  const [salvataggio, setSalvataggio] = useState(false);

  const isDirty = form !== null && JSON.stringify(form) !== initialSnapshot;
  useDirtyForm(isDirty, `Conferma ricarica n. ${riga.transactionId}`);

  // Applica un dettaglio appena letto: il modulo di conferma esiste solo per le anomale
  const applica = useCallback((d: DettaglioSessione, nota = '') => {
    setDettaglio(d);
    if (d.stato === 'ANOMALA' && canEdit) {
      setForm(formDa(d, nota));
      // Lo snapshot ha la nota vuota: un motivo già scritto resta "da salvare"
      setInitialSnapshot(JSON.stringify(formDa(d)));
    } else {
      setForm(null);
      setInitialSnapshot('');
    }
  }, [canEdit]);

  const idSessione = riga.id;
  useEffect(() => {
    let annullato = false;
    (async () => {
      try {
        const d = await leggiDettaglio(idSessione);
        if (!annullato) applica(d);
      } catch (err) {
        if (annullato) return;
        showToast(messaggioErrore(err, 'Errore nel caricamento della ricarica'), 'error');
        onChiudi();
      } finally {
        if (!annullato) setCaricamento(false);
      }
    })();
    return () => { annullato = true; };
  }, [idSessione, applica, onChiudi]);

  const chiudiDaSfondo = () => {
    // Con dati non salvati un clic fuori dalla modale non deve farli perdere
    if (!isDirty && !salvataggio) onChiudi();
  };

  // Il dettaglio letto non è più valido: lo si rilegge e si avvisa chi sta confermando
  const ricaricaDopoCambiamento = async (messaggioServer: string) => {
    if (!dettaglio) return;
    try {
      const d = await leggiDettaglio(dettaglio.id);
      applica(d, form?.nota ?? '');
      setAvviso(d.stato === 'ANOMALA'
        ? `${messaggioServer}. Ho riletto i dati aggiornati: controllali e conferma di nuovo.`
        : `La ricarica è cambiata nel frattempo ed è ora ${STATO_SESSIONE[d.stato].label.toLowerCase()}: non serve più confermarla.`);
      onAggiornata();
    } catch (err) {
      setErrore(`${messaggioServer}. Non sono riuscito a rileggerla: ${messaggioErrore(err, 'errore di caricamento')}`);
    }
  };

  const conferma = async () => {
    if (!dettaglio || !form) return;
    setAvviso('');
    setErrore('');

    if (!form.dipendenteId) { setErrore('Scegli il dipendente a cui intestare la ricarica'); return; }

    const testoKwh = form.energiaKwh.trim().replace(',', '.');
    const kwh = Number(testoKwh);
    if (testoKwh === '' || !Number.isFinite(kwh) || kwh < 0) {
      setErrore('Energia non valida: indica i kWh erogati (0 se nessuno)');
      return;
    }
    const energiaWh = Math.round(kwh * 1000);
    if (energiaWh > ENERGIA_MAX_WH) { setErrore('Energia non plausibile: al massimo 1.000 kWh'); return; }

    // La fine si chiede solo se la sessione non ne ha una
    let fineAt: string | null = null;
    if (dettaglio.fineAt === null) {
      if (!form.fineAt) { setErrore('Indica quando è finita la ricarica'); return; }
      const fine = new Date(form.fineAt);
      if (Number.isNaN(fine.getTime())) { setErrore('Data e ora di fine non valide'); return; }
      if (fine.getTime() < new Date(dettaglio.inizioAt).getTime()) {
        setErrore("La fine non può essere precedente all'inizio");
        return;
      }
      if (fine.getTime() > Date.now()) { setErrore('La fine non può essere nel futuro'); return; }
      fineAt = fine.toISOString();
    }

    // Il prezzo si chiede solo se per la società della colonnina non c'è una tariffa valida
    let prezzoKwh: number | null = null;
    if (dettaglio.tariffaMancante) {
      const testoPrezzo = form.prezzoKwh.trim().replace(',', '.');
      const prezzo = Number(testoPrezzo);
      const centesimi = prezzo * 100;
      if (testoPrezzo === '' || !Number.isFinite(prezzo) || prezzo <= 0 || prezzo > PREZZO_MAX
        || Math.abs(centesimi - Math.round(centesimi)) > 1e-6) {
        setErrore('Prezzo non valido: da 0,01 a 9999,99 €/kWh, al massimo 2 decimali');
        return;
      }
      prezzoKwh = prezzo;
    }

    const nota = form.nota.trim();
    if (!nota) { setErrore('Scrivi il motivo della conferma: resta nel registro'); return; }
    if (nota.length > NOTA_MAX) { setErrore(`Il motivo è troppo lungo: al massimo ${NOTA_MAX} caratteri`); return; }

    const corpo = {
      dipendenteId: Number(form.dipendenteId),
      energiaWh,
      fineAt,
      prezzoKwh,
      nota,
      // Esattamente quello letto nel dettaglio (anche null): il server rifiuta se nel frattempo è cambiato
      ultimoEventoAt: dettaglio.ultimoEventoAt ?? null,
    };

    setSalvataggio(true);
    try {
      // @api POST /api/ricarica/sessioni/:id/conferma {dipendenteId,energiaWh,fineAt,prezzoKwh,nota,ultimoEventoAt} -> {success:true,data:{id,transactionId,stato,energiaWh,prezzoKwh,importo,competenza}}
      await apiFetch<{ success: boolean; data: DettaglioSessione }>(`/api/ricarica/sessioni/${dettaglio.id}/conferma`, {
        method: 'POST',
        body: JSON.stringify(corpo),
      });
      setInitialSnapshot(JSON.stringify(form));
      showToast('Ricarica confermata', 'success');
      onAggiornata();
      onChiudi();
    } catch (err) {
      const e = err as ErroreApi;
      if (e.status === 409 && /cambiata nel frattempo/i.test(e.message)) {
        await ricaricaDopoCambiamento('La ricarica è cambiata nel frattempo');
      } else {
        setErrore(messaggioErrore(err, 'Errore nella conferma della ricarica'));
      }
    } finally {
      setSalvataggio(false);
    }
  };

  const opzioniDipendente = useMemo(() => {
    const attuale = dettaglio?.dipendente;
    if (attuale && !dipendenti.some((d) => d.id === attuale.id)) {
      return [{ id: attuale.id, nome: attuale.nome, cognome: attuale.cognome }, ...dipendenti];
    }
    return dipendenti;
  }, [dettaglio, dipendenti]);

  const d = dettaglio;
  const anomalaConfermabile = d !== null && d.stato === 'ANOMALA' && form !== null;

  return (
    <div className="bop-modal-overlay" onClick={chiudiDaSfondo}>
      <div className="bop-modal bop-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="bop-modal__header">
          <h2 className="bop-modal__title">Ricarica n. {riga.transactionId}</h2>
          <button className="bop-modal__close" onClick={onChiudi} disabled={salvataggio} aria-label="Chiudi">✕</button>
        </div>
        <div className="bop-modal__body bop-ricarica__form">
          {caricamento || !d ? (
            <div className="inline-loading">Caricamento…</div>
          ) : (
            <>
              {avviso && <div className="bop-alert bop-alert--warning bop-mb-0">{avviso}</div>}
              <div className="bop-ricarica__campi">
                <Campo label="Colonnina">{d.colonnina.nome} · presa {d.connettore}</Campo>
                <Campo label="Società della colonnina">
                  {d.colonnina.societaNome}{d.colonnina.sede ? ` · ${d.colonnina.sede}` : ''}
                </Campo>
                <Campo label="Badge"><span className="bop-text-mono">{d.codiceBadge}</span></Campo>
                <Campo label="Dipendente">
                  {d.dipendente ? `${nomeCompleto(d.dipendente)} (${d.dipendente.societaNome})` : 'Non riconosciuto'}
                </Campo>
                <Campo label="Inizio">{formatDataOra(d.inizioAt)}</Campo>
                <Campo label="Fine">
                  {d.fineAt ? formatDataOra(d.fineAt) : d.stato === 'IN_CORSO' ? 'In corso' : 'Non registrata'}
                </Campo>
                <Campo label="Energia">
                  {d.energiaWh !== null ? `${formatNumber(whInKwh(d.energiaWh))} kWh` : '—'}
                </Campo>
                <Campo label="Tariffa">
                  {d.prezzoKwh !== null ? `${formatCurrency(d.prezzoKwh)} €/kWh` : '—'}
                </Campo>
                <Campo label="Importo">{d.importo !== null ? `${formatCurrency(d.importo)} €` : '—'}</Campo>
                <Campo label="Mese di riaddebito">
                  {d.competenza ? etichettaMese(d.competenza.anno, d.competenza.mese) : 'Non ancora assegnato'}
                </Campo>
                <Campo label="Contatore all'inizio e alla fine">
                  {formatNumber(whInKwh(d.meterStartWh))} → {d.meterStopWh !== null ? formatNumber(whInKwh(d.meterStopWh)) : '—'} kWh
                </Campo>
                <Campo label="Ultimo evento">{formatDataOra(d.ultimoEventoAt)}</Campo>
                {d.motivoStop && <Campo label="Motivo dello stop">{d.motivoStop}</Campo>}
              </div>

              {d.tardiva && (
                <div className="bop-alert bop-alert--warning bop-mb-0">
                  Ricarica arrivata dopo la chiusura del mese in cui è finita: conteggiata nel primo mese aperto.
                </div>
              )}

              {d.stato === 'ANOMALA' && (
                <>
                  <div className="bop-alert bop-alert--danger bop-mb-0">{d.motivoAnomalia}</div>
                  {anomalaConfermabile && form && (
                    <>
                      <p className="bop-ricarica__nota">
                        Una ricarica anomala non entra nel riaddebito finché non viene confermata. Controlla i dati,
                        correggili se serve e scrivi il motivo: resta nel registro.
                      </p>
                      {errore && <div className="bop-alert bop-alert--danger bop-mb-0">{errore}</div>}
                      <div className="bop-form-row">
                        <div className="bop-field">
                          <label className="bop-field__label" htmlFor="conf-dip">
                            Dipendente <span className="bop-field__required">*</span>
                          </label>
                          <select id="conf-dip" className="bop-field__select" value={form.dipendenteId}
                            onChange={(e) => setForm({ ...form, dipendenteId: e.target.value })}>
                            <option value="">Scegli…</option>
                            {opzioniDipendente.map((o) => (
                              <option key={o.id} value={o.id}>{nomeCompleto(o)}</option>
                            ))}
                          </select>
                        </div>
                        <div className="bop-field">
                          <label className="bop-field__label" htmlFor="conf-kwh">
                            Energia kWh <span className="bop-field__required">*</span>
                          </label>
                          <input id="conf-kwh" type="number" min="0" step="0.01" className="bop-field__input"
                            value={form.energiaKwh}
                            onChange={(e) => setForm({ ...form, energiaKwh: e.target.value })} />
                        </div>
                      </div>
                      {(d.fineAt === null || d.tariffaMancante) && (
                        <div className="bop-form-row">
                          {d.fineAt === null && (
                            <div className="bop-field">
                              <label className="bop-field__label" htmlFor="conf-fine">
                                Fine della ricarica <span className="bop-field__required">*</span>
                              </label>
                              <input id="conf-fine" type="datetime-local" className="bop-field__input"
                                value={form.fineAt}
                                onChange={(e) => setForm({ ...form, fineAt: e.target.value })} />
                              <p className="bop-field__hint">
                                La ricarica non ha una fine registrata. Si può confermare solo se l&apos;ultimo evento
                                risale a più di 24 ore fa.
                              </p>
                            </div>
                          )}
                          {d.tariffaMancante && (
                            <div className="bop-field">
                              <label className="bop-field__label" htmlFor="conf-prezzo">
                                Prezzo €/kWh <span className="bop-field__required">*</span>
                              </label>
                              <input id="conf-prezzo" type="number" min="0.01" max={PREZZO_MAX} step="0.01"
                                className="bop-field__input" value={form.prezzoKwh}
                                onChange={(e) => setForm({ ...form, prezzoKwh: e.target.value })} />
                              <p className="bop-field__hint">
                                Per la società della colonnina non c&apos;è una tariffa valida alla data di inizio:
                                indica il prezzo da applicare.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="bop-field">
                        <label className="bop-field__label" htmlFor="conf-nota">
                          Motivo <span className="bop-field__required">*</span>
                        </label>
                        <textarea id="conf-nota" className="bop-field__textarea" rows={3} maxLength={NOTA_MAX}
                          placeholder="Es. badge personale di Rossi non ancora registrato, verificato con lui"
                          value={form.nota}
                          onChange={(e) => setForm({ ...form, nota: e.target.value })} />
                        <p className="bop-field__hint">{form.nota.length}/{NOTA_MAX} caratteri</p>
                      </div>
                    </>
                  )}
                </>
              )}

              {d.log.length > 0 && (
                <>
                  <div className="bop-section-label">Registro</div>
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th>Quando</th>
                        <th>Utente</th>
                        <th>Azione</th>
                        <th>Dettaglio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.log.map((v, i) => (
                        <tr key={`${v.createdAt}-${i}`}>
                          <td>{formatDataOra(v.createdAt)}</td>
                          <td>{v.utenteName}</td>
                          <td>{v.azione}</td>
                          <td className="bop-text-muted">{v.dettaglio ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onChiudi} disabled={salvataggio}>Chiudi</button>
          {anomalaConfermabile && (
            <button className="bop-btn bop-btn-primary" onClick={conferma} disabled={salvataggio}>
              {salvataggio ? 'Conferma in corso…' : 'Conferma ricarica'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Pagina ───────────────────────────────────────────────────────────────────

export default function SessioniRicaricaPage() {
  const { can } = usePermesso();
  const canEdit = can(MOD_SESSIONI, 'edit');
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const mesi = useMemo(mesiElenco, []);

  const [elenco, setElenco] = useState<ElencoSessioni | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [erroreElenco, setErroreElenco] = useState('');

  const [colonnine, setColonnine] = useState<ColonninaNome[]>([]);
  const [sedi, setSedi] = useState<SedeOpzione[]>([]);
  const [dipendenti, setDipendenti] = useState<DipendenteOpzione[]>([]);

  const [fMese, setFMese] = useState(mesi[0].valore);
  const [fStato, setFStato] = useState<'' | StatoSessione>('');
  const [fColonnina, setFColonnina] = useState('');
  const [fSede, setFSede] = useState('');
  const [fDipendente, setFDipendente] = useState('');
  const [fQ, setFQ] = useState('');
  const [qApplicata, setQApplicata] = useState('');

  const [aperta, setAperta] = useState<Sessione | null>(null);

  const richiestaElenco = useRef(0);

  // Cambio società: filtri, opzioni e dettaglio aperto appartengono a quella di prima
  useEffect(() => {
    setFColonnina('');
    setFSede('');
    setFDipendente('');
    setColonnine([]);
    setSedi([]);
    setDipendenti([]);
    setElenco(null);
    setAperta(null);
  }, [societaId]);

  // La ricerca parte poco dopo l'ultimo carattere digitato
  useEffect(() => {
    const t = setTimeout(() => setQApplicata(fQ.trim()), 300);
    return () => clearTimeout(t);
  }, [fQ]);

  // Opzioni dei filtri colonnina, sede e dipendente
  useEffect(() => {
    if (!pronta) return;
    let annullato = false;
    const qs = societaId ? `?societaId=${encodeURIComponent(societaId)}` : '';
    const segnala = (err: unknown, ripiego: string) => {
      if (!annullato) showToast(messaggioErrore(err, ripiego), 'error');
    };

    (async () => {
      try {
        // @api GET /api/ricarica/colonnine/elenco?societaId -> {success:true,data:[{id,nome,sede}]}
        const res = await apiFetch<{ success: boolean; data: ColonninaNome[] }>(`/api/ricarica/colonnine/elenco${qs}`);
        if (!annullato) setColonnine(res.data);
      } catch (err) { segnala(err, 'Errore nel caricamento delle colonnine'); }
    })();
    (async () => {
      try {
        // @api GET /api/ricarica/sedi?societaId -> {success:true,data:[{id,nome}]}
        const res = await apiFetch<{ success: boolean; data: SedeOpzione[] }>(`/api/ricarica/sedi${qs}`);
        if (!annullato) setSedi(res.data);
      } catch (err) { segnala(err, 'Errore nel caricamento delle sedi'); }
    })();
    (async () => {
      try {
        // @api GET /api/ricarica/dipendenti?societaId -> {success:true,data:[{id,nome,cognome}]}
        const res = await apiFetch<{ success: boolean; data: DipendenteOpzione[] }>(`/api/ricarica/dipendenti${qs}`);
        if (!annullato) setDipendenti(res.data);
      } catch (err) { segnala(err, 'Errore nel caricamento dei dipendenti'); }
    })();

    return () => { annullato = true; };
  }, [pronta, societaId]);

  const caricaElenco = useCallback(async () => {
    if (!pronta) return;
    const numero = ++richiestaElenco.current;
    setCaricamento(true);
    try {
      const p = new URLSearchParams();
      if (societaId) p.set('societaId', societaId);
      const mese = mesi.find((m) => m.valore === fMese);
      if (mese) {
        p.set('anno', String(mese.anno));
        p.set('mese', String(mese.mese));
      }
      if (fStato) p.set('stato', fStato);
      if (fColonnina) p.set('colonninaId', fColonnina);
      if (fSede) p.set('sedeId', fSede);
      if (fDipendente) p.set('dipendenteId', fDipendente);
      if (qApplicata) p.set('q', qApplicata);
      // @api GET /api/ricarica/sessioni?societaId&anno&mese&stato&colonninaId&sedeId&dipendenteId&q -> {success:true,data:{sessioni:[{id,transactionId,colonnina,connettore,dipendente,codiceBadge,inizioAt,fineAt,energiaWh,prezzoKwh,importo,stato,motivoAnomalia,tardiva,competenza}],troncato,totali:{sessioni,energiaWh,importo,anomale}}}
      const res = await apiFetch<{ success: boolean; data: ElencoSessioni }>(`/api/ricarica/sessioni?${p}`);
      if (numero !== richiestaElenco.current) return;
      setElenco(res.data);
      setErroreElenco('');
    } catch (err) {
      if (numero !== richiestaElenco.current) return;
      setElenco(null);
      setErroreElenco(messaggioErrore(err, 'Errore nel caricamento delle ricariche'));
    } finally {
      if (numero === richiestaElenco.current) setCaricamento(false);
    }
  }, [pronta, societaId, mesi, fMese, fStato, fColonnina, fSede, fDipendente, qApplicata]);

  useEffect(() => { caricaElenco(); }, [caricaElenco]);

  const chiudiDettaglio = useCallback(() => setAperta(null), []);

  const sessioni = elenco?.sessioni ?? [];

  return (
    <Layout>
      <div className="bop-ricarica__stack">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2 className="bop-page-title">Sessioni di ricarica</h2>
            <p className="bop-page-subtitle">
              Ogni ricarica compare sia alla società della colonnina sia a quella del dipendente
            </p>
          </div>
        </div>

        {elenco && (
          <div className="bop-ricarica__kpi-grid">
            <Kpi label="Ricariche concluse" valore={String(elenco.totali.sessioni)} />
            <Kpi label="Energia" valore={`${formatNumber(whInKwh(elenco.totali.energiaWh))} kWh`} />
            <Kpi label="Importo" valore={`${formatCurrency(elenco.totali.importo)} €`} />
            <Kpi label="Anomale" valore={String(elenco.totali.anomale)} />
          </div>
        )}

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
              <option value="">Tutte le società</option>
              {listaSocieta.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
            </select>
          )}
          <select className="bop-select" value={fMese} onChange={(e) => setFMese(e.target.value)}>
            <option value="">Tutti i mesi</option>
            {mesi.map((m) => (<option key={m.valore} value={m.valore}>{etichettaMese(m.anno, m.mese)}</option>))}
          </select>
          <select className="bop-select" value={fSede} onChange={(e) => setFSede(e.target.value)}>
            <option value="">Tutte le sedi</option>
            {sedi.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
          </select>
          <select className="bop-select" value={fColonnina} onChange={(e) => setFColonnina(e.target.value)}>
            <option value="">Tutte le colonnine</option>
            {colonnine.map((c) => (<option key={c.id} value={c.id}>{c.nome}</option>))}
          </select>
          <select className="bop-select" value={fDipendente} onChange={(e) => setFDipendente(e.target.value)}>
            <option value="">Tutti i dipendenti</option>
            {dipendenti.map((d) => (<option key={d.id} value={d.id}>{nomeCompleto(d)}</option>))}
          </select>
          <select className="bop-select" value={fStato} onChange={(e) => setFStato(e.target.value as '' | StatoSessione)}>
            <option value="">Tutti gli stati</option>
            <option value="IN_CORSO">In corso</option>
            <option value="CONCLUSA">Concluse</option>
            <option value="ANOMALA">Anomale</option>
          </select>
          <input className="bop-input" placeholder="Cerca dipendente, colonnina o badge…" value={fQ}
            onChange={(e) => setFQ(e.target.value)} />
        </div>

        {elenco?.troncato && (
          <div className="bop-alert bop-alert--warning bop-mb-0">
            Sono mostrate solo le 1000 ricariche più recenti: restringi i filtri per vedere le altre. I totali comprendono
            tutte le ricariche filtrate.
          </div>
        )}

        {erroreElenco && <div className="bop-alert bop-alert--danger bop-mb-0">{erroreElenco}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {!pronta ? (
              <div className="bop-empty-state">
                <p className="bop-empty-state__text">Seleziona una società in alto per vedere le ricariche</p>
              </div>
            ) : caricamento && !elenco ? (
              <div className="inline-loading">Caricamento…</div>
            ) : sessioni.length === 0 ? (
              <div className="bop-empty-state">
                <h3 className="bop-empty-state__title">Nessuna ricarica</h3>
                <p className="bop-empty-state__text">Nessuna ricarica trovata con i filtri selezionati</p>
              </div>
            ) : (
              <table className="bop-table bop-table--clickable">
                <thead>
                  <tr>
                    <th>Inizio</th>
                    <th>Colonnina</th>
                    <th>Dipendente</th>
                    <th className="bop-text-right">kWh</th>
                    <th className="bop-text-right">Importo</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {sessioni.map((s) => (
                    <tr key={s.id} onClick={() => setAperta(s)}>
                      <td>{formatDataOra(s.inizioAt)}</td>
                      <td>
                        {s.colonnina.nome}
                        <div className="bop-ricarica__secondaria">
                          {s.colonnina.societaNome}{s.colonnina.sede ? ` · ${s.colonnina.sede}` : ''}
                        </div>
                      </td>
                      <td>
                        {s.dipendente ? (
                          <>
                            {nomeCompleto(s.dipendente)}
                            <div className="bop-ricarica__secondaria">{s.dipendente.societaNome}</div>
                          </>
                        ) : (
                          <span className="bop-text-danger">Badge {s.codiceBadge}</span>
                        )}
                      </td>
                      <td className="bop-text-right">{s.energiaWh !== null ? formatNumber(whInKwh(s.energiaWh)) : '—'}</td>
                      <td className="bop-text-right">{s.importo !== null ? `${formatCurrency(s.importo)} €` : '—'}</td>
                      <td>
                        <span className="bop-ricarica__connettori">
                          <span className={`bop-badge ${STATO_SESSIONE[s.stato].badge}`}>{STATO_SESSIONE[s.stato].label}</span>
                          {s.tardiva && <span className="bop-badge bop-badge--warning">Tardiva</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {aperta && (
          <ModaleSessione
            key={aperta.id}
            riga={aperta}
            dipendenti={dipendenti}
            canEdit={canEdit}
            onChiudi={chiudiDettaglio}
            onAggiornata={caricaElenco}
          />
        )}
      </div>
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
